import { createSlice, PayloadAction } from '@reduxjs/toolkit'

import { getDefaultAlertDateRange } from '../../utils/alertDateDefaults'

export interface IAlertsFilterState {
    searchValue: string
    selectedColumn: {
        attributeName: string
        columnName: string
        attributeType?: string
        dataType?: string
    } | null
    dateRange: [string | null, string | null]
    severityFilter: string[]
    alertTypeFilter: string[]
    readStatusFilter: string[]
    productFilter: any[] // Store the selected product objects
    columnFilters: {
        id: string
        value: any
    }[]
    sortParams: {
        orderAttribute: string
        orderBy: 'ASC' | 'DESC'
    }
}

const initialState: IAlertsFilterState = {
    searchValue: '',
    selectedColumn: null,
    // Default to the last 60 days — matches the window the backend applies anyway when no range is
    // sent (see alertDateDefaults). Recomputed on reset so a long-lived session stays current.
    dateRange: getDefaultAlertDateRange(),
    severityFilter: [],
    alertTypeFilter: [],
    readStatusFilter: [],
    productFilter: [],
    columnFilters: [],
    sortParams: {
        orderAttribute: 'time',
        orderBy: 'DESC',
    },
}

const alertsSlice = createSlice({
    name: 'alerts',
    initialState,
    reducers: {
        // Update any part of the state
        setAlertsFilters(
            state,
            action: PayloadAction<Partial<IAlertsFilterState>>,
        ) {
            const next = { ...state, ...action.payload }
            // An empty [null, null] range must not suppress the default. It reaches us two ways:
            // saved alert preferences written by earlier builds (login hydration merges them over
            // initialState), and the persist effect, which deliberately stores [null, null] rather
            // than freezing the default. Either way, resolve it to a freshly computed window — a
            // range the user actually picked is left untouched.
            if (next.dateRange && !next.dateRange[0] && !next.dateRange[1]) {
                next.dateRange = getDefaultAlertDateRange()
            }
            return next
        },
        // Reset to default (useful for "Clear All Filters" or Logout).
        // dateRange is recomputed rather than reused from initialState so the default window is
        // relative to now, not to when the app bundle was loaded.
        resetAlertsFilters() {
            return { ...initialState, dateRange: getDefaultAlertDateRange() }
        },
    },
})

export const { setAlertsFilters, resetAlertsFilters } = alertsSlice.actions
export default alertsSlice.reducer
